import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import _ from 'lodash.debounce';
import styles from './PickerView.module.scss';

interface Props {
  data: { label: string | number; value: string | number }[];
  value: string | number | undefined;
  onScroll: (value: string | number) => void;
}

export default function (props: Props) {
  const { data, onScroll, value } = props;
  const [start, setStart] = useState<number>(0);
  const [move, setMove] = useState<number>(0);
  const [_move, setBackUpMove] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const listRef = useRef<HTMLUListElement>(null);
  const isTouchEnd = useRef<boolean>();
  const timer = useRef<NodeJS.Timeout>();
  const wheelRef = useRef<HTMLDivElement>(null);
  // 根据根的字体大小计算出88px具体对应高度
  const itemHeight = Math.ceil(
    (Number(getComputedStyle(document.documentElement).fontSize.slice(0, -2)) /
      100) *
      88
  );
  const maxHeight = itemHeight * data.length;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const spring = useCallback(
    _(function (moveDistance, _moveDistance, offset, callback, isTouchEnd) {
      if (!isTouchEnd.current) {
        return false;
      }

      const move = moveDistance + _moveDistance;

      setBackUpMove(_move + move);
      if (
        Math.abs(move) < itemHeight * 2 ||
        offset + Math.abs(move) >= maxHeight ||
        offset - move < 0
      ) {
        callback(move, offset);
      } else {
        const distance = Math.abs(move);
        const step = Math.ceil(distance / 4);
        let timer: NodeJS.Timeout | undefined = undefined;
        let count = 0;

        if (timer) {
          window.clearInterval(timer);
          callback(move, offset);
        } else {
          timer = setInterval(function () {
            if (count >= distance) {
              clearInterval(timer!);
              callback(move + distance * (move > 0 ? 1 : -1), offset);
            } else {
              count += step;
              setMove(move + Math.min(count, distance) * (move > 0 ? 1 : -1));
            }
          }, 50);
        }
      }
    }, 16),
    []
  );

  const handleTouchEnd = useCallback(
    function (move: number, offset: number) {
      const result = Math.max(
        Math.min(offset + move, 0),
        -maxHeight + itemHeight
      );
      const rest = Math.abs(result % itemHeight);

      const calcOffset =
        result +
        (rest > itemHeight / 2 ? itemHeight - rest : -rest) *
          (result < 0 ? -1 : 1);

      const index = Math.abs(calcOffset) / itemHeight;

      setOffset(calcOffset);
      setMove(0);
      setBackUpMove(0);
      onScroll(data[index].value);
    },
    [data, itemHeight, maxHeight, onScroll]
  );

  const handleTouchMove = useCallback(
    function (e: React.TouchEvent) {
      // 记录移动的相对距离
      const move = e.targetTouches[0].pageY - start;

      isTouchEnd.current = false;
      e.stopPropagation();
      setMove(move);
      spring(move, _move, offset, handleTouchEnd, isTouchEnd);
    },
    [_move, handleTouchEnd, isTouchEnd, offset, spring, start]
  );

  const handleTouchStart = useCallback(function (e: React.TouchEvent) {
    // 记录起始点
    e.stopPropagation();
    setStart(e.targetTouches[0].pageY);
  }, []);

  useEffect(
    function () {
      let index = 0;

      if (value) {
        index = data.findIndex(item => item.value === value);
      }
      setOffset((index > -1 ? -index : 0) * itemHeight);
    },
    [data, itemHeight, value]
  );

  useEffect(function () {
    function preventDefault(e: TouchEvent) {
      e.preventDefault();
    }
    if (wheelRef.current) {
      wheelRef.current.addEventListener('touchmove', preventDefault, {
        passive: false,
      });
    }

    return function () {
      timer.current && window.clearTimeout(timer.current);
      if (wheelRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        wheelRef.current.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  return (
    <div
      ref={wheelRef}
      className={styles.picker_content}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchCancel={() => handleTouchEnd(move, offset)}
      onTouchEnd={() => {
        isTouchEnd.current = true;
        timer.current && window.clearTimeout(timer.current);
        timer.current = setTimeout(function () {
          if (move !== 0) {
            handleTouchEnd(move, offset);
          }
        }, 200);
      }}
    >
      <div className={styles.picker_mask} />
      <div
        className={styles.picker_select_line}
        style={{ height: itemHeight }}
      />
      <div className={styles.picker_list} style={{ height: itemHeight }}>
        <ul
          style={{
            transform: `translateY(${offset + move}px) `,
            // transition: isMoving?'all 0.3s cubic-bezier(0, 0, 0.2, 1.15) 0s':,
          }}
          className={styles.picker_wheel}
          ref={listRef}
        >
          {data.map(function (item, index) {
            const isCurrent = !itemHeight
              ? !index
              : index === Math.abs(offset / itemHeight);

            return (
              <li
                className={cn(
                  styles.picker_item,
                  isCurrent ? styles.picker_activeItem : null
                )}
                style={{ height: itemHeight, lineHeight: itemHeight + 'px' }}
                key={item.value}
              >
                <span>{item.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
